/**
 * Hook for managing session timeout and auto-logout
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { errorLogger } from '@/lib/errorLogger';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // Show warning 5 minutes before

interface UseSessionTimeoutOptions {
  onWarning?: () => void;
  onLogout?: () => void;
}

/**
 * Track user activity and auto-logout when inactive
 */
export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const { user, signOut } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const warningTimeoutRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());

  const { onWarning, onLogout } = options;

  const resetTimeout = useCallback(() => {
    if (!user) return;

    lastActivityRef.current = Date.now();

    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

    // Set warning timeout (5 minutes before actual timeout)
    warningTimeoutRef.current = setTimeout(() => {
      onWarning?.();
      errorLogger.info('Session timeout warning shown', { userId: user.id });
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS);

    // Set logout timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        await signOut();
        onLogout?.();
        errorLogger.info('User auto-logged out due to inactivity', { userId: user.id });
      } catch (error) {
        errorLogger.error('Failed to auto-logout user', error as Error, { userId: user.id });
      }
    }, SESSION_TIMEOUT_MS);
  }, [user, signOut, onWarning, onLogout]);

  useEffect(() => {
    if (!user) return;

    // Set initial timeout
    resetTimeout();

    // Track user activity
    const activities = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      resetTimeout();
    };

    // Add listeners
    activities.forEach(activity => {
      document.addEventListener(activity, handleActivity, true);
    });

    // Cleanup
    return () => {
      activities.forEach(activity => {
        document.removeEventListener(activity, handleActivity, true);
      });

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [user, resetTimeout]);

  return {
    getLastActivity: () => lastActivityRef.current,
    getTimeUntilLogout: () => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;
      return Math.max(0, SESSION_TIMEOUT_MS - elapsed);
    },
  };
}
